import {
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
	TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, ChildrenOutletContexts } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { LoginService } from './login-service/login.service';
import { routeTransitionAnimations } from './animations/route-animations';
import { PDFService } from './pdf-service/pdf.service';
import { DemoEvent } from './components/game-play-demo/game-play-demo.component';
import { EditorGameDefinition } from './EditorGameDefinition';
import { EditorGameDefinitionService } from './services/editor-game-definition.service';
import { PluginType } from '@meta-press/shared-plugins';
import { NotificationContainerComponent } from './notification/notification-container.component';
import { environment } from '../../environments/environment';
import { UpdateStatusService } from './update-status-service/update-status.service';
import { NotificationService } from './notification-service/notification.service';
import { NotificationHandle } from './notification/notification-handle';

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [CommonModule, RouterOutlet, NotificationContainerComponent],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	animations: [routeTransitionAnimations],
})
export class AppComponent implements OnInit, OnDestroy {
	private updateCheckInterval?: number;
	// Handle for the update notification to prevent duplicates
	private updateNotificationHandle: NotificationHandle | null = null;

	// Template reference for demo modal
	@ViewChild('demoModal') demoModalTemplate!: TemplateRef<any>;

	// Demo data
	demoGameDefinition: EditorGameDefinition | null = null;
	demoEvents: DemoEvent[] = [];

	constructor(
		protected loginService: LoginService,
		private router: Router,
		private contexts: ChildrenOutletContexts,
		private swUpdate: SwUpdate,
		private pdfService: PDFService, // Preload PDF worker on app startup
		private modalService: NgbModal,
		private editorGameDefinitionService: EditorGameDefinitionService,
		private updateStatusService: UpdateStatusService,
		private notificationService: NotificationService,
	) {
		this.loginService.OnLogin.subscribe({
			next: (user) => {
				if (user) {
					this.router.navigate(['/article-library']);
				}
			},
			error: (error) => {
				console.error('Login error:', error);
				// Handle login error if needed
			},
		});

		// Auto-reload when new version is available
		if (swUpdate.isEnabled) {
			console.log(
				'[UPDATE] Service worker is enabled, subscribing to version updates',
			);
			swUpdate.versionUpdates.subscribe((event) => {
				console.log(
					'[UPDATE] Version update event received:',
					event.type,
				);
				if (event.type === 'VERSION_READY') {
					console.log(
						'[UPDATE] VERSION_READY event - New version available, checking login status...',
					);
					this.autoUpdateIfOnLoginScreen();
				}
				if (event.type === 'VERSION_INSTALLATION_FAILED') {
					console.error(
						'[UPDATE] VERSION_INSTALLATION_FAILED - Failed to install new version',
					);
				}
			});

			// Check for updates every 60 minutes
			this.updateCheckInterval = window.setInterval(
				() => {
					console.log('Checking for app updates...');
					this.swUpdate
						.checkForUpdate()
						.then((updateFound) => {
							if (updateFound) {
								console.log(
									'Update found during periodic check',
								);
								this.promptUserToUpdate();
							}
						})
						.catch((err) => {
							console.error('Error checking for updates:', err);
						});
				},
				60 * 60 * 1000,
			); // 60 minutes in milliseconds
		}

		// Handle unrecoverable state
		this.swUpdate.unrecoverable.subscribe((event) => {
			console.error('Unrecoverable state:', event.reason);
			this.reloadApp('Application is in an unrecoverable state');
		});
	}

	ngOnInit(): void {
		console.log('[AppComponent] ngOnInit called');

		// Check for fresh install by comparing version in localStorage
		const lastKnownVersion = localStorage.getItem('lastAppVersion');
		const currentVersion = `${environment.appVersion} ${environment.appVersionPostfix}`;

		// Check if app was just updated (either automatically or manually)
		const appWasUpdated = sessionStorage.getItem('appWasUpdated');

		console.log(
			'[UPDATE] Flag check in app-content ngOnInit:',
			appWasUpdated,
		);

		console.log('[UPDATE] Flag === "auto"?', appWasUpdated === 'auto');
		console.log('[UPDATE] Flag === "manual"?', appWasUpdated === 'manual');

		if (appWasUpdated === 'auto') {
			console.log('[UPDATE] Showing auto-update toast...');
			this.notificationService.ShowToast(
				'Sovellus päivitettiin uuteen versioon automaattisesti.',
				'success',
			);
		}

		console.log('[VERSION] Last known version:', lastKnownVersion);
		console.log('[VERSION] Current version:', currentVersion);

		if (!lastKnownVersion) {
			// Fresh install - user has never opened the app before
			console.log('[VERSION] Fresh install detected, setting flag');
			sessionStorage.setItem('appInstallType', 'fresh-install');
		}

		// Store current version for next time
		localStorage.setItem('lastAppVersion', currentVersion);

		// Check if update flag exists after reload
		const flagAfterReload = sessionStorage.getItem('appWasUpdated');
		console.log(
			'[UPDATE] Flag check in app.component ngOnInit:',
			flagAfterReload,
		);

		// TEST: Add a test notification with progress to test minimize timer
		// this.notificationService.createNotification(
		// 	'Testing minimize timer - hover to pause, mouseout to resume',
		// 	{
		// 		style: 'info',
		// 		timeout: -1,
		// 		progress: 50,
		// 	},
		// );

		// Check for updates immediately when component initializes
		if (this.swUpdate.isEnabled) {
			console.log('Checking for app updates on initialization...');
			// Set status to checking before starting update check
			this.updateStatusService.setStatus('checking');
			this.swUpdate
				.checkForUpdate()
				.then((updateFound) => {
					if (updateFound) {
						console.log('Update found on initialization');
						// Set status to update-available
						this.updateStatusService.setStatus('update-available');
						this.autoUpdateIfOnLoginScreen();
					} else {
						console.log('No updates found on initialization');
						// Set status to up-to-date
						this.updateStatusService.setStatus('up-to-date');
					}
				})
				.catch((err) => {
					console.error(
						'Error checking for updates on initialization:',
						err,
					);
					// On error, set status to up-to-date (no update found)
					this.updateStatusService.setStatus('up-to-date');
				});
		}

		// Show demo modal after a short delay to ensure app is initialized
		// setTimeout(() => {
		// 	console.log('[AppComponent] Triggering demo modal...');
		// 	this.showDemoModal();
		// }, 1000);
	}

	// Method to get route animation data
	getRouteAnimationData() {
		return this.contexts.getContext('primary')?.route?.snapshot?.data?.[
			'animation'
		];
	}

	/**
	 * Show demo modal with MultiChoice game tutorial
	 */
	showDemoModal(): void {
		console.log('[AppComponent] showDemoModal called');
		// Find the MultiChoice game definition
		const multiChoiceGame =
			this.editorGameDefinitionService.editorGameDefinitions.find(
				(game) => game.type === PluginType.MultiChoice,
			);

		if (!multiChoiceGame) {
			console.error(
				'[AppComponent] MultiChoice game definition not found',
			);
			return;
		}

		console.log('[AppComponent] MultiChoice game found:', multiChoiceGame);
		this.demoGameDefinition = multiChoiceGame;

		// Create demo events for the tutorial
		this.demoEvents = [
			{
				type: 'text',
				text: '<p>Tervetuloa! Tämä on esimerkki monivalintapelistä. Peli kysyy sinulta kysymyksiä. </p><p>Katsotaanpa, miten kysymykseen vastataan.</p>',
			},
			{
				type: 'reset',
			},
			{
				type: 'cursor',
				selector: '.answer-option',
				index: 0,
				click: true,
			},
			{
				type: 'text',
				text: 'Hienoa! Kokeillaan vielä toista vaihtoehtoa.',
			},
			{
				type: 'reset',
			},
			{
				type: 'cursor',
				selector: '.answer-option',
				index: 1,
				click: true,
			},
			{
				type: 'text',
				text: 'Näin monivalintapeli toimii! Voit nyt sulkea tämän ikkunan.',
			},
		];
		console.log('[AppComponent] Demo events created:', this.demoEvents);

		// Open the modal
		console.log(
			'[AppComponent] Opening modal with template:',
			this.demoModalTemplate,
		);
		const modalRef = this.modalService.open(this.demoModalTemplate, {
			size: 'xl',
			centered: true,
			backdrop: 'static',
		});
		console.log('[AppComponent] Modal opened, reference:', modalRef);
	}

	/**
	 * Auto-update if on login screen, otherwise prompt user
	 */
	private autoUpdateIfOnLoginScreen(): void {
		const currentUser = this.loginService.GetCurrentUser();

		// If no user is logged in (on login screen), auto-update immediately
		if (!currentUser) {
			console.log('[UPDATE] User not logged in, auto-updating...');
			// Set flag before reload so we can show notification after
			console.log(
				'[UPDATE] Setting sessionStorage flag: appWasUpdated = auto',
			);
			sessionStorage.setItem('appWasUpdated', 'auto');
			const flagCheck = sessionStorage.getItem('appWasUpdated');
			console.log('[UPDATE] Flag verification after setting:', flagCheck);

			// Activate update and reload
			if (this.swUpdate.isEnabled) {
				console.log(
					'[UPDATE] Service worker is enabled, calling activateUpdate()',
				);
				this.swUpdate
					.activateUpdate()
					.then(() => {
						console.log(
							'[UPDATE] activateUpdate() resolved successfully',
						);
						const flagBeforeReload =
							sessionStorage.getItem('appWasUpdated');
						console.log(
							'[UPDATE] Flag value before reload:',
							flagBeforeReload,
						);
						// Add small delay to ensure sessionStorage is persisted
						setTimeout(() => {
							console.log('[UPDATE] About to reload page...');
							window.location.reload();
						}, 100);
					})
					.catch((error) => {
						console.error(
							'[UPDATE] Failed to activate update:',
							error,
						);
						// Clean up flag if activation failed
						sessionStorage.removeItem('appWasUpdated');
					});
			} else {
				console.log('[UPDATE] Service worker is NOT enabled!');
			}
		} else {
			// User is logged in, show prompt banner instead
			console.log('User logged in, showing update prompt...');
			this.promptUserToUpdate();
		}
	}

	private promptUserToUpdate(): void {
		// Check if notification already exists to avoid duplicates
		if (this.updateNotificationHandle) {
			console.log('Update notification already shown');
			return;
		}

		// Create notification with action button for updating
		this.updateNotificationHandle =
			this.notificationService.createNotification(
				'Uusi versio on saatavilla!',
				{
					style: 'info',
					showCloseButton: false,
					timeout: -1,
					actionButton: {
						buttonText: 'Päivitä',
						iconClass: 'bi bi-arrow-clockwise',
						callback: () => this.activateUpdate(),
					},
				},
			);
	}

	private activateUpdate(): void {
		if (this.swUpdate.isEnabled) {
			// Set flag before reload to track manual update
			sessionStorage.setItem('appWasUpdated', 'manual');

			this.swUpdate
				.activateUpdate()
				.then(() => {
					this.reloadApp('Application updated successfully');
				})
				.catch((error) => {
					console.error('Failed to activate update:', error);
					// Clean up flag if activation failed
					sessionStorage.removeItem('appWasUpdated');
				});
		}
	}

	private reloadApp(reason: string): void {
		console.log('Reloading app:', reason);
		// Close the update notification if it exists
		if (this.updateNotificationHandle) {
			this.updateNotificationHandle.close();
			this.updateNotificationHandle = null;
		}
		// Reload the page
		window.location.reload();
	}

	// Public method to manually check for updates
	public manualUpdateCheck(): Promise<boolean> {
		if (this.swUpdate.isEnabled) {
			return this.swUpdate.checkForUpdate();
		}
		return Promise.resolve(false);
	}

	ngOnDestroy(): void {
		// Clean up the interval when component is destroyed
		if (this.updateCheckInterval) {
			clearInterval(this.updateCheckInterval);
		}
	}
}
